import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../services/api';

/**
 * Media Library Page
 * 
 * Comprehensive interface for managing all generated and uploaded media assets.
 * Features:
 * - Grid/list view toggle
 * - Advanced filtering (type, date, project, tags, favorites)
 * - Full-text search
 * - Bulk selection and operations
 * - Quick preview modal
 * - Collection management
 * - Usage tracking
 * - Storage statistics
 */

const MEDIA_TYPES = [
  { value: 'image', label: 'Images', icon: '🖼️' },
  { value: 'video', label: 'Videos', icon: '🎬' },
  { value: 'audio', label: 'Audio', icon: '🎵' }
];

const GENERATORS = [
  { value: 'stable_diffusion', label: 'Stable Diffusion' },
  { value: 'dall_e', label: 'DALL-E' },
  { value: 'replicate', label: 'Replicate' },
  { value: 'stable_video', label: 'Stable Video' },
  { value: 'runway', label: 'RunwayML' },
  { value: 'uploaded', label: 'Uploaded' }
];

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Date Created' },
  { value: 'title', label: 'Title' },
  { value: 'file_size', label: 'File Size' },
  { value: 'usage_count', label: 'Most Used' }
];

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const MediaLibrary = () => {
  // View state
  const [viewMode, setViewMode] = useState('grid'); // grid | list
  const [showFilters, setShowFilters] = useState(true);
  
  // Data state
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [collections, setCollections] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(24);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    media_types: [],
    generators: [],
    tags: [],
    is_favorite: null,
    date_from: '',
    date_to: '',
    search_query: ''
  });
  
  // Sort
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Modals
  const [showPreview, setShowPreview] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  
  // Search
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounce, setSearchDebounce] = useState(null);

  const API_BASE = '/api/media';

  // Fetch media
  const fetchMedia = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('per_page', perPage.toString());
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      
      filters.media_types.forEach(t => params.append('media_types', t));
      filters.generators.forEach(g => params.append('generators', g));
      filters.tags.forEach(t => params.append('tags', t));
      
      if (filters.is_favorite !== null) {
        params.append('is_favorite', filters.is_favorite.toString());
      }
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.search_query) params.append('search_query', filters.search_query);
      
      const response = await authFetch(`${API_BASE}/library?${params}`);
      if (!response.ok) throw new Error('Failed to fetch media');
      
      const data = await response.json();
      setMedia(data.media || []);
      setTotalItems(data.total || 0);
      setTotalPages(data.total_pages || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, sortBy, sortOrder, filters]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  // Fetch collections
  const fetchCollections = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/collections`);
      if (response.ok) {
        const data = await response.json();
        setCollections(data.collections || []);
      }
    } catch (err) {
      console.error('Failed to fetch collections:', err);
    }
  }, []);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  useEffect(() => {
    fetchStats();
    fetchCollections();
  }, [fetchStats, fetchCollections]);

  // Search debounce
  useEffect(() => {
    if (searchDebounce) clearTimeout(searchDebounce);
    
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, search_query: searchInput }));
      setPage(1);
    }, 500);
    
    setSearchDebounce(timeout);
    
    return () => clearTimeout(timeout);
  }, [searchInput]);

  // Toggle favorite
  const toggleFavorite = async (mediaId) => {
    try {
      const response = await authFetch(`${API_BASE}/library/${mediaId}/favorite`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        setMedia(prev => prev.map(m => 
          m.id === mediaId ? { ...m, is_favorite: data.is_favorite } : m
        ));
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  // Delete media
  const deleteMedia = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const response = await authFetch(`${API_BASE}/library/${mediaId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setMedia(prev => prev.filter(m => m.id !== mediaId));
        setSelectedIds(prev => {
          const next = new Set(prev);
          next.delete(mediaId);
          return next;
        });
        fetchStats();
      }
    } catch (err) {
      console.error('Failed to delete media:', err);
    }
  };

  // Bulk operations
  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    
    const mediaIds = Array.from(selectedIds);
    
    try {
      const response = await authFetch(`${API_BASE}/library/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          media_ids: mediaIds,
          operation: action
        })
      });
      
      if (response.ok) {
        fetchMedia();
        setSelectedIds(new Set());
        setSelectAll(false);
        fetchStats();
      }
    } catch (err) {
      console.error('Bulk operation failed:', err);
    }
  };

  // Toggle selection
  const toggleSelect = (mediaId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(mediaId)) {
        next.delete(mediaId);
      } else {
        next.add(mediaId);
      }
      return next;
    });
  };

  // Select all on page
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(media.map(m => m.id)));
    }
    setSelectAll(!selectAll);
  };

  // Create collection
  const createCollection = async () => {
    if (!newCollectionName.trim()) return;
    
    try {
      const response = await authFetch(`${API_BASE}/collections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCollectionName })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCollections(prev => [...prev, data.collection]);
        setNewCollectionName('');
        setShowCollectionModal(false);
      }
    } catch (err) {
      console.error('Failed to create collection:', err);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      media_types: [],
      generators: [],
      tags: [],
      is_favorite: null,
      date_from: '',
      date_to: '',
      search_query: ''
    });
    setSearchInput('');
    setPage(1);
  };

  // Styles
  const styles = {
    container: {
      display: 'flex',
      height: 'calc(100vh - 64px)',
      background: '#f9fafb',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    sidebar: {
      width: showFilters ? '280px' : '0px',
      background: 'white',
      borderRight: '1px solid #e5e7eb',
      overflow: 'hidden',
      transition: 'width 0.3s',
      flexShrink: 0
    },
    sidebarContent: {
      width: '280px',
      padding: '20px',
      height: '100%',
      overflowY: 'auto'
    },
    main: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: {
      padding: '20px 24px',
      background: 'white',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1f2937',
      margin: 0
    },
    searchContainer: {
      flex: 1,
      maxWidth: '400px',
      marginLeft: '24px'
    },
    searchInput: {
      width: '100%',
      padding: '10px 16px 10px 40px',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '14px',
      background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'%3E%3C/path%3E%3C/svg%3E") no-repeat 12px center`,
      backgroundSize: '20px'
    },
    headerActions: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center'
    },
    toolbar: {
      padding: '12px 24px',
      background: 'white',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    toolbarLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    toolbarRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    button: {
      padding: '8px 16px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'all 0.2s'
    },
    primaryButton: {
      background: '#3b82f6',
      color: 'white'
    },
    secondaryButton: {
      background: '#f3f4f6',
      color: '#374151',
      border: '1px solid #d1d5db'
    },
    dangerButton: {
      background: '#ef4444',
      color: 'white'
    },
    iconButton: {
      padding: '8px',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      background: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    activeIconButton: {
      background: '#3b82f6',
      color: 'white',
      border: '1px solid #3b82f6'
    },
    content: {
      flex: 1,
      padding: '24px',
      overflowY: 'auto'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
      gap: '20px'
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      cursor: 'pointer',
      border: '2px solid transparent'
    },
    cardSelected: {
      border: '2px solid #3b82f6'
    },
    cardImage: {
      width: '100%',
      height: '180px',
      objectFit: 'cover',
      background: '#f3f4f6'
    },
    cardBody: {
      padding: '16px'
    },
    cardTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '8px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    cardMeta: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '12px',
      color: '#6b7280'
    },
    cardActions: {
      display: 'flex',
      gap: '8px',
      marginTop: '12px'
    },
    listItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '16px',
      background: 'white',
      borderRadius: '8px',
      gap: '16px',
      cursor: 'pointer',
      border: '2px solid transparent'
    },
    listItemSelected: {
      border: '2px solid #3b82f6'
    },
    listItemThumb: {
      width: '80px',
      height: '60px',
      objectFit: 'cover',
      borderRadius: '4px',
      background: '#f3f4f6'
    },
    listItemContent: {
      flex: 1
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer'
    },
    filterSection: {
      marginBottom: '24px'
    },
    filterTitle: {
      fontSize: '12px',
      fontWeight: '600',
      color: '#6b7280',
      textTransform: 'uppercase',
      marginBottom: '12px'
    },
    filterOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 0',
      cursor: 'pointer',
      fontSize: '14px',
      color: '#374151'
    },
    select: {
      width: '100%',
      padding: '8px 12px',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      fontSize: '14px',
      background: 'white'
    },
    dateInput: {
      width: '100%',
      padding: '8px 12px',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      fontSize: '14px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
      marginBottom: '24px'
    },
    statCard: {
      padding: '12px',
      background: '#f9fafb',
      borderRadius: '8px',
      textAlign: 'center'
    },
    statValue: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#1f2937'
    },
    statLabel: {
      fontSize: '11px',
      color: '#6b7280',
      marginTop: '4px'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      padding: '20px',
      borderTop: '1px solid #e5e7eb',
      background: 'white'
    },
    pageButton: {
      padding: '8px 12px',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      background: 'white',
      cursor: 'pointer',
      fontSize: '14px'
    },
    activePageButton: {
      background: '#3b82f6',
      color: 'white',
      border: '1px solid #3b82f6'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    previewModal: {
      background: 'white',
      borderRadius: '16px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    },
    previewImage: {
      maxWidth: '100%',
      maxHeight: '70vh',
      objectFit: 'contain'
    },
    previewInfo: {
      padding: '20px',
      background: 'white',
      borderTop: '1px solid #e5e7eb'
    },
    previewTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '12px'
    },
    previewMeta: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '16px',
      marginBottom: '16px'
    },
    previewMetaItem: {
      fontSize: '13px'
    },
    previewMetaLabel: {
      color: '#6b7280',
      marginBottom: '4px'
    },
    previewMetaValue: {
      fontWeight: '500',
      color: '#1f2937'
    },
    closeButton: {
      position: 'absolute',
      top: '16px',
      right: '16px',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: 'rgba(0,0,0,0.5)',
      color: 'white',
      border: 'none',
      cursor: 'pointer',
      fontSize: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#6b7280'
    },
    emptyIcon: {
      fontSize: '48px',
      marginBottom: '16px'
    },
    badge: {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: '500',
      background: '#e0e7ff',
      color: '#4338ca'
    },
    favButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '18px',
      padding: '4px'
    }
  };

  // Render filter sidebar
  const renderSidebar = () => (
    <div style={styles.sidebar}>
      <div style={styles.sidebarContent}>
        {/* Stats */}
        {stats && (
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{stats.total_count || 0}</div>
              <div style={styles.statLabel}>Total Files</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{formatFileSize(stats.total_size_bytes)}</div>
              <div style={styles.statLabel}>Storage</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{stats.image_count || 0}</div>
              <div style={styles.statLabel}>Images</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{stats.video_count || 0}</div>
              <div style={styles.statLabel}>Videos</div>
            </div>
          </div>
        )}

        {/* Media Type Filter */}
        <div style={styles.filterSection}>
          <div style={styles.filterTitle}>Media Type</div>
          {MEDIA_TYPES.map(type => (
            <label key={type.value} style={styles.filterOption}>
              <input
                type="checkbox"
                checked={filters.media_types.includes(type.value)}
                onChange={e => {
                  const types = e.target.checked
                    ? [...filters.media_types, type.value]
                    : filters.media_types.filter(t => t !== type.value);
                  setFilters(prev => ({ ...prev, media_types: types }));
                  setPage(1);
                }}
              />
              <span>{type.icon}</span>
              <span>{type.label}</span>
            </label>
          ))}
        </div>

        {/* Generator Filter */}
        <div style={styles.filterSection}>
          <div style={styles.filterTitle}>Source</div>
          {GENERATORS.map(gen => (
            <label key={gen.value} style={styles.filterOption}>
              <input
                type="checkbox"
                checked={filters.generators.includes(gen.value)}
                onChange={e => {
                  const gens = e.target.checked
                    ? [...filters.generators, gen.value]
                    : filters.generators.filter(g => g !== gen.value);
                  setFilters(prev => ({ ...prev, generators: gens }));
                  setPage(1);
                }}
              />
              <span>{gen.label}</span>
            </label>
          ))}
        </div>

        {/* Favorites Filter */}
        <div style={styles.filterSection}>
          <div style={styles.filterTitle}>Status</div>
          <label style={styles.filterOption}>
            <input
              type="checkbox"
              checked={filters.is_favorite === true}
              onChange={e => {
                setFilters(prev => ({ 
                  ...prev, 
                  is_favorite: e.target.checked ? true : null 
                }));
                setPage(1);
              }}
            />
            <span>⭐</span>
            <span>Favorites Only</span>
          </label>
        </div>

        {/* Date Range */}
        <div style={styles.filterSection}>
          <div style={styles.filterTitle}>Date Range</div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ fontSize: '12px', color: '#6b7280' }}>From</label>
            <input
              type="date"
              style={styles.dateInput}
              value={filters.date_from}
              onChange={e => {
                setFilters(prev => ({ ...prev, date_from: e.target.value }));
                setPage(1);
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#6b7280' }}>To</label>
            <input
              type="date"
              style={styles.dateInput}
              value={filters.date_to}
              onChange={e => {
                setFilters(prev => ({ ...prev, date_to: e.target.value }));
                setPage(1);
              }}
            />
          </div>
        </div>

        {/* Collections */}
        <div style={styles.filterSection}>
          <div style={{ ...styles.filterTitle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Collections</span>
            <button
              style={{ ...styles.button, padding: '4px 8px', fontSize: '12px' }}
              onClick={() => setShowCollectionModal(true)}
            >
              +
            </button>
          </div>
          {collections.map(col => (
            <div key={col.id} style={styles.filterOption}>
              <span>📁</span>
              <span>{col.name}</span>
              <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#9ca3af' }}>
                {col.media_count || 0}
              </span>
            </div>
          ))}
        </div>

        {/* Clear Filters */}
        <button
          style={{ ...styles.button, ...styles.secondaryButton, width: '100%', justifyContent: 'center' }}
          onClick={clearFilters}
        >
          Clear All Filters
        </button>
      </div>
    </div>
  );

  // Render media card (grid view)
  const renderGridCard = (item) => (
    <div
      key={item.id}
      style={{
        ...styles.card,
        ...(selectedIds.has(item.id) ? styles.cardSelected : {})
      }}
      onClick={() => {
        setPreviewMedia(item);
        setShowPreview(true);
      }}
    >
      {/* Thumbnail */}
      <div style={{ position: 'relative' }}>
        {item.media_type === 'video' ? (
          <div style={{ ...styles.cardImage, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px' }}>
            🎬
          </div>
        ) : (
          <img
            src={item.thumbnail_url || item.file_url || '/placeholder.png'}
            alt={item.title}
            style={styles.cardImage}
            onError={e => { e.target.style.background = '#e5e7eb'; }}
          />
        )}
        
        {/* Selection checkbox */}
        <div
          style={{ position: 'absolute', top: '8px', left: '8px' }}
          onClick={e => {
            e.stopPropagation();
            toggleSelect(item.id);
          }}
        >
          <input
            type="checkbox"
            style={styles.checkbox}
            checked={selectedIds.has(item.id)}
            onChange={() => {}}
          />
        </div>
        
        {/* Favorite button */}
        <button
          style={{ ...styles.favButton, position: 'absolute', top: '8px', right: '8px' }}
          onClick={e => {
            e.stopPropagation();
            toggleFavorite(item.id);
          }}
        >
          {item.is_favorite ? '⭐' : '☆'}
        </button>
        
        {/* Type badge */}
        <div style={{ position: 'absolute', bottom: '8px', left: '8px' }}>
          <span style={styles.badge}>
            {item.media_type?.toUpperCase()}
          </span>
        </div>
      </div>
      
      <div style={styles.cardBody}>
        <div style={styles.cardTitle}>
          {item.title || 'Untitled'}
        </div>
        <div style={styles.cardMeta}>
          <span>{formatDate(item.created_at)}</span>
          <span>{formatFileSize(item.file_size)}</span>
        </div>
        {item.prompt && (
          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.prompt.substring(0, 50)}...
          </div>
        )}
      </div>
    </div>
  );

  // Render media item (list view)
  const renderListItem = (item) => (
    <div
      key={item.id}
      style={{
        ...styles.listItem,
        ...(selectedIds.has(item.id) ? styles.listItemSelected : {})
      }}
      onClick={() => {
        setPreviewMedia(item);
        setShowPreview(true);
      }}
    >
      <input
        type="checkbox"
        style={styles.checkbox}
        checked={selectedIds.has(item.id)}
        onClick={e => e.stopPropagation()}
        onChange={() => toggleSelect(item.id)}
      />
      
      {item.media_type === 'video' ? (
        <div style={{ ...styles.listItemThumb, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          🎬
        </div>
      ) : (
        <img
          src={item.thumbnail_url || item.file_url || '/placeholder.png'}
          alt={item.title}
          style={styles.listItemThumb}
        />
      )}
      
      <div style={styles.listItemContent}>
        <div style={{ fontWeight: '600', color: '#1f2937' }}>{item.title || 'Untitled'}</div>
        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
          {item.generator} • {formatFileSize(item.file_size)} • {formatDate(item.created_at)}
        </div>
      </div>
      
      <span style={styles.badge}>{item.media_type}</span>
      
      <button
        style={styles.favButton}
        onClick={e => {
          e.stopPropagation();
          toggleFavorite(item.id);
        }}
      >
        {item.is_favorite ? '⭐' : '☆'}
      </button>
      
      <button
        style={{ ...styles.iconButton, color: '#ef4444' }}
        onClick={e => {
          e.stopPropagation();
          deleteMedia(item.id);
        }}
      >
        🗑️
      </button>
    </div>
  );

  // Render preview modal
  const renderPreviewModal = () => {
    if (!showPreview || !previewMedia) return null;
    
    return (
      <div style={styles.modal} onClick={() => setShowPreview(false)}>
        <div style={{ ...styles.previewModal, position: 'relative' }} onClick={e => e.stopPropagation()}>
          <button style={styles.closeButton} onClick={() => setShowPreview(false)}>×</button>
          
          {previewMedia.media_type === 'video' ? (
            <video
              src={previewMedia.file_url}
              controls
              style={{ maxWidth: '100%', maxHeight: '70vh' }}
            />
          ) : (
            <img
              src={previewMedia.file_url || '/placeholder.png'}
              alt={previewMedia.title}
              style={styles.previewImage}
            />
          )}
          
          <div style={styles.previewInfo}>
            <div style={styles.previewTitle}>{previewMedia.title || 'Untitled'}</div>
            
            <div style={styles.previewMeta}>
              <div style={styles.previewMetaItem}>
                <div style={styles.previewMetaLabel}>Type</div>
                <div style={styles.previewMetaValue}>{previewMedia.media_type}</div>
              </div>
              <div style={styles.previewMetaItem}>
                <div style={styles.previewMetaLabel}>Size</div>
                <div style={styles.previewMetaValue}>{formatFileSize(previewMedia.file_size)}</div>
              </div>
              <div style={styles.previewMetaItem}>
                <div style={styles.previewMetaLabel}>Dimensions</div>
                <div style={styles.previewMetaValue}>{previewMedia.width} × {previewMedia.height}</div>
              </div>
              <div style={styles.previewMetaItem}>
                <div style={styles.previewMetaLabel}>Created</div>
                <div style={styles.previewMetaValue}>{formatDate(previewMedia.created_at)}</div>
              </div>
              <div style={styles.previewMetaItem}>
                <div style={styles.previewMetaLabel}>Generator</div>
                <div style={styles.previewMetaValue}>{previewMedia.generator || 'Uploaded'}</div>
              </div>
              <div style={styles.previewMetaItem}>
                <div style={styles.previewMetaLabel}>Uses</div>
                <div style={styles.previewMetaValue}>{previewMedia.usage_count || 0}</div>
              </div>
            </div>
            
            {previewMedia.prompt && (
              <div>
                <div style={styles.previewMetaLabel}>Prompt</div>
                <div style={{ fontSize: '13px', color: '#374151', padding: '8px', background: '#f9fafb', borderRadius: '6px' }}>
                  {previewMedia.prompt}
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={() => window.open(previewMedia.file_url, '_blank')}
              >
                Download
              </button>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => {
                  navigator.clipboard.writeText(previewMedia.prompt || '');
                }}
              >
                Copy Prompt
              </button>
              <button
                style={{ ...styles.button, ...styles.dangerButton }}
                onClick={() => {
                  deleteMedia(previewMedia.id);
                  setShowPreview(false);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render collection modal
  const renderCollectionModal = () => {
    if (!showCollectionModal) return null;
    
    return (
      <div style={styles.modal} onClick={() => setShowCollectionModal(false)}>
        <div
          style={{ background: 'white', borderRadius: '12px', padding: '24px', width: '400px' }}
          onClick={e => e.stopPropagation()}
        >
          <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>Create Collection</h3>
          <input
            type="text"
            placeholder="Collection name"
            value={newCollectionName}
            onChange={e => setNewCollectionName(e.target.value)}
            style={{ ...styles.searchInput, marginBottom: '16px', paddingLeft: '12px', background: 'white' }}
          />
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              style={{ ...styles.button, ...styles.secondaryButton }}
              onClick={() => setShowCollectionModal(false)}
            >
              Cancel
            </button>
            <button
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={createCollection}
            >
              Create
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return (
      <div style={styles.pagination}>
        <button
          style={styles.pageButton}
          disabled={page === 1}
          onClick={() => setPage(1)}
        >
          ««
        </button>
        <button
          style={styles.pageButton}
          disabled={page === 1}
          onClick={() => setPage(p => p - 1)}
        >
          «
        </button>
        
        {pages.map(p => (
          <button
            key={p}
            style={{
              ...styles.pageButton,
              ...(p === page ? styles.activePageButton : {})
            }}
            onClick={() => setPage(p)}
          >
            {p}
          </button>
        ))}
        
        <button
          style={styles.pageButton}
          disabled={page === totalPages}
          onClick={() => setPage(p => p + 1)}
        >
          »
        </button>
        <button
          style={styles.pageButton}
          disabled={page === totalPages}
          onClick={() => setPage(totalPages)}
        >
          »»
        </button>
        
        <span style={{ marginLeft: '16px', fontSize: '14px', color: '#6b7280' }}>
          {totalItems} items
        </span>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      {renderSidebar()}
      
      {/* Main Content */}
      <div style={styles.main}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <button
              style={styles.iconButton}
              onClick={() => setShowFilters(!showFilters)}
              title={showFilters ? 'Hide filters' : 'Show filters'}
            >
              {showFilters ? '◀' : '▶'}
            </button>
            <h1 style={styles.title}>Media Library</h1>
          </div>
          
          <div style={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search by title, description, or prompt..."
              style={styles.searchInput}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>
          
          <div style={styles.headerActions}>
            <button style={{ ...styles.button, ...styles.primaryButton }}>
              📤 Upload
            </button>
          </div>
        </div>
        
        {/* Toolbar */}
        <div style={styles.toolbar}>
          <div style={styles.toolbarLeft}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                style={styles.checkbox}
                checked={selectAll}
                onChange={toggleSelectAll}
              />
              <span style={{ fontSize: '14px' }}>Select All</span>
            </label>
            
            {selectedIds.size > 0 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={() => handleBulkAction('favorite')}
                >
                  ⭐ Favorite ({selectedIds.size})
                </button>
                <button
                  style={{ ...styles.button, ...styles.dangerButton }}
                  onClick={() => handleBulkAction('delete')}
                >
                  🗑️ Delete ({selectedIds.size})
                </button>
              </div>
            )}
          </div>
          
          <div style={styles.toolbarRight}>
            <select
              style={styles.select}
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            
            <button
              style={styles.iconButton}
              onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
              title={sortOrder === 'asc' ? 'Sort ascending' : 'Sort descending'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
            
            <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
              <button
                style={{
                  ...styles.iconButton,
                  border: 'none',
                  borderRadius: 0,
                  ...(viewMode === 'grid' ? styles.activeIconButton : {})
                }}
                onClick={() => setViewMode('grid')}
              >
                ⊞
              </button>
              <button
                style={{
                  ...styles.iconButton,
                  border: 'none',
                  borderRadius: 0,
                  ...(viewMode === 'list' ? styles.activeIconButton : {})
                }}
                onClick={() => setViewMode('list')}
              >
                ☰
              </button>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div style={styles.content}>
          {loading ? (
            <div style={styles.emptyState}>
              <div>Loading media...</div>
            </div>
          ) : error ? (
            <div style={{ ...styles.emptyState, color: '#ef4444' }}>
              <div>Error: {error}</div>
            </div>
          ) : media.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🖼️</div>
              <h3>No media found</h3>
              <p>Upload or generate some content to get started</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div style={styles.grid}>
              {media.map(renderGridCard)}
            </div>
          ) : (
            <div style={styles.list}>
              {media.map(renderListItem)}
            </div>
          )}
        </div>
        
        {/* Pagination */}
        {renderPagination()}
      </div>
      
      {/* Modals */}
      {renderPreviewModal()}
      {renderCollectionModal()}
    </div>
  );
};

export default MediaLibrary;
